import { RouteRecordRaw } from 'vue-router'
import {
  ContentBlockMap,
  EContentBlockPosition,
  PlaceholderKey,
} from 'src/models/Content/PlaceholderKey'
import { ROUTES, RouteName, CustomRouteMeta } from 'src/models/route'

type StrictRoute = Omit<RouteRecordRaw, 'meta' | 'children'> & {
  meta?: CustomRouteMeta
  children?: StrictRoute[]
}
function defineRoutes<T extends CustomRouteMeta>(
  routes: Array<
    Omit<RouteRecordRaw, 'meta' | 'children'> & {
      meta?: T
      children?: StrictRoute[]
    }
  >,
): Array<RouteRecordRaw & { meta: T }> {
  return routes as Array<RouteRecordRaw & { meta: T }>
}

const nonProdOnlyRoutes = [
  {
    name: RouteName.DEMO,
    path: ROUTES[RouteName.DEMO].path,
    component: () => import('pages/MlDemo.vue'),
  },
]

const defaultContentBlockMeta = {
  'content-block': {
    [EContentBlockPosition.Top]: [PlaceholderKey.GlobalTop],
    [EContentBlockPosition.Bottom]: [PlaceholderKey.GlobalBottom],
  },
}

function applyDefaultContentBlock(meta?: ContentBlockMap) {
  const result: ContentBlockMap = {
    [EContentBlockPosition.Top]: [],
    [EContentBlockPosition.Bottom]: [],
  }
  const positions = Object.values(EContentBlockPosition)

  for (const pos of positions) {
    result[pos] = [
      ...(defaultContentBlockMeta['content-block'][pos] ?? []),
      ...(meta?.[pos] ?? []),
    ]
  }
  return result
}

const routes = defineRoutes([
  {
    name: RouteName.ROOT,
    path: ROUTES[RouteName.ROOT].path,
    component: () => import('layouts/MlLayout.vue'),
    children: [
      {
        name: RouteName.HOME,
        path: ROUTES[RouteName.HOME].path,
        component: () => import('pages/MlBookLocker.vue'),
        meta: {
          'content-block': applyDefaultContentBlock({
            [EContentBlockPosition.Top]: [PlaceholderKey.HomepageTop],
            [EContentBlockPosition.Bottom]: [PlaceholderKey.HomepageBottom],
          }),
        },
      },
      {
        name: RouteName.EVENT,
        path: ROUTES[RouteName.EVENT].path,
        component: () => import('pages/MlBookLocker.vue'),
        meta: {
          'content-block': applyDefaultContentBlock({
            [EContentBlockPosition.Top]: [PlaceholderKey.HomepageTop],
            [EContentBlockPosition.Bottom]: [PlaceholderKey.HomepageBottom],
          }),
        },
      },
      {
        name: RouteName.LOCATION,
        path: ROUTES[RouteName.LOCATION].path,
        component: () => import('pages/MlBookLocker.vue'),
        meta: {
          'content-block': applyDefaultContentBlock({
            [EContentBlockPosition.Top]: [PlaceholderKey.HomepageTop],
            [EContentBlockPosition.Bottom]: [PlaceholderKey.HomepageBottom],
          }),
        },
      },
      {
        name: RouteName.RENTAL,
        path: ROUTES[RouteName.RENTAL].path,
        component: () => import('../pages/Rental/MlRental.vue'),
        children: [
          {
            name: RouteName.RENTAL_DURATION,
            path: ROUTES[RouteName.RENTAL_DURATION].path,
            component: () =>
              import('components/content/rental/MlRentalDuration.vue'),
            meta: {
              'content-block': applyDefaultContentBlock({
                [EContentBlockPosition.Top]: [
                  PlaceholderKey.BookingRentalDurationTop,
                ],
                [EContentBlockPosition.Bottom]: [
                  PlaceholderKey.BookingRentalDurationBottom,
                ],
              }),
            },
          },
          {
            name: RouteName.RENTAL_LOCKER_TYPE,
            path: ROUTES[RouteName.RENTAL_LOCKER_TYPE].path,
            component: () =>
              import('components/content/rental/MlRentalLockerType.vue'),
            meta: {
              'content-block': applyDefaultContentBlock({
                [EContentBlockPosition.Top]: [
                  PlaceholderKey.BookingRentalLockerTypeTop,
                ],
                [EContentBlockPosition.Bottom]: [
                  PlaceholderKey.BookingRentalLockerTypeBottom,
                ],
              }),
            },
          },
        ],
      },
      {
        name: RouteName.RESERVATION,
        path: ROUTES[RouteName.RESERVATION].path,
        component: () => import('pages/Reservation/MlReservation.vue'),
        children: [
          {
            name: RouteName.RESERVATION_DURATION,
            path: ROUTES[RouteName.RESERVATION_DURATION].path,
            component: () =>
              import('components/content/reservation/MlReservationDuration.vue'),
            meta: {
              'content-block': applyDefaultContentBlock({
                [EContentBlockPosition.Top]: [
                  PlaceholderKey.BookingReservationDurationTop,
                ],
                [EContentBlockPosition.Bottom]: [
                  PlaceholderKey.BookingReservationDurationBottom,
                ],
              }),
            },
          },
          {
            name: RouteName.RESERVATION_LOCKER_TYPE,
            path: ROUTES[RouteName.RESERVATION_LOCKER_TYPE].path,
            component: () =>
              import('components/content/reservation/MlReservationLockerType.vue'),
            meta: {
              'content-block': applyDefaultContentBlock({
                [EContentBlockPosition.Top]: [
                  PlaceholderKey.BookingReservationLockerTypeTop,
                ],
                [EContentBlockPosition.Bottom]: [
                  PlaceholderKey.BookingReservationLockerTypeBottom,
                ],
              }),
            },
          },
        ],
      },
      {
        name: RouteName.RESERVATION_PICKUP,
        path: ROUTES[RouteName.RESERVATION_PICKUP].path,
        component: () =>
          import('pages/ReservationPickup/MlPickupReservation.vue'),
        meta: {
          'content-block': applyDefaultContentBlock({
            [EContentBlockPosition.Top]: [PlaceholderKey.ReservationPickupTop],
            [EContentBlockPosition.Bottom]: [
              PlaceholderKey.ReservationPickupBottom,
            ],
          }),
        },
      },
      {
        name: RouteName.CART,
        path: ROUTES[RouteName.CART].path,
        component: () => import('pages/Cart/MlCart.vue'),
        meta: {
          'content-block': applyDefaultContentBlock({
            [EContentBlockPosition.Top]: [PlaceholderKey.ShoppingCartTop],
            [EContentBlockPosition.Bottom]: [PlaceholderKey.ShoppingCartBottom],
          }),
        },
      },
      {
        name: RouteName.CHECKOUT,
        path: ROUTES[RouteName.CHECKOUT].path,
        component: () => import('pages/Cart/MlCheckout.vue'),
        meta: {
          'content-block': applyDefaultContentBlock(),
        },
      },
      {
        name: RouteName.PAYMENT_VERIFICATION,
        path: ROUTES[RouteName.PAYMENT_VERIFICATION].path,
        component: () => import('pages/Payment/MlPaymentVerification.vue'),
        meta: {
          'content-block': applyDefaultContentBlock(),
        },
        props: true,
      },
      {
        name: RouteName.ORDER_OVERVIEW,
        path: ROUTES[RouteName.ORDER_OVERVIEW].path,
        component: () => import('pages/Order/MlOrderOverview.vue'),
        meta: {
          'content-block': applyDefaultContentBlock(),
        },
        props: true,
      },
      {
        name: RouteName.RENTAL_DETAIL,
        path: ROUTES[RouteName.RENTAL_DETAIL].path,
        component: () => import('pages/Order/Rental/MlRentalDetail.vue'),
        props: true,
        meta: {
          'content-block': applyDefaultContentBlock({
            [EContentBlockPosition.Top]: [PlaceholderKey.OrderRentalDetailsTop],
            [EContentBlockPosition.Bottom]: [
              PlaceholderKey.OrderRentalDetailsBottom,
            ],
          }),
        },
      },
      {
        name: RouteName.RESERVATION_DETAIL,
        path: ROUTES[RouteName.RESERVATION_DETAIL].path,
        component: () =>
          import('pages/Order/Reservation/MlReservationDetail.vue'),
        props: true,
        meta: {
          'content-block': applyDefaultContentBlock({
            [EContentBlockPosition.Top]: [
              PlaceholderKey.OrderReservationDetailsTop,
            ],
            [EContentBlockPosition.Bottom]: [
              PlaceholderKey.OrderReservationDetailsBottom,
            ],
          }),
        },
      },
      {
        name: RouteName.MY_ORDERS,
        path: ROUTES[RouteName.MY_ORDERS].path,
        component: () => import('pages/User/MlMyOrders.vue'),
        meta: {
          'content-block': applyDefaultContentBlock({
            [EContentBlockPosition.Top]: [PlaceholderKey.OrderMyOrdersTop],
            [EContentBlockPosition.Bottom]: [
              PlaceholderKey.OrderMyOrdersBottom,
            ],
          }),
        },
      },
      ...(process.env.NODE_ENV !== 'production' ? nonProdOnlyRoutes : []),
    ],
  },
  {
    name: RouteName.SERVICE_UNAVAILABLE,
    path: ROUTES[RouteName.SERVICE_UNAVAILABLE].path,
    component: () => import('pages/ErrorServiceUnavailable.vue'),
  },

  // Always leave this as last one,
  // but you can also remove it
  {
    name: RouteName.NOT_FOUND,
    path: ROUTES[RouteName.NOT_FOUND].path,
    component: () => import('pages/ErrorNotFound.vue'),
  },
])

export default routes
